<template>
  <v-dialog v-model="dialog" scrollable persistent width="1000">
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      :top="true"
    >
      {{ snackbarMsg }}
      <v-btn dark text @click="showSnackbar = false">CLOSE</v-btn>
    </v-snackbar>
    <v-card flat tile color="white">
      <v-card-title class="dialog-header-title">{{ title }}</v-card-title>
      <v-card-text
        class="py-0"
        v-if="selectedCompany != null"
        style="height: 450px"
      >
        <v-row no-gutters>
          <v-col cols="12">
            <v-row>
              <v-col cols="12" sm="6" md="5">
                <span>Company name:</span>
                <input class="form-control" v-model="selectedCompany.name" />
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <span>Short name:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.short_name"
                />
              </v-col>

              <v-col cols="12" sm="6" md="2">
                <span>Reg. number:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.registration_number"
                />
              </v-col>

              <v-col cols="12" sm="6" md="2">
                <span>Currency:</span>
                <v-select
                  :items="currencies"
                  solo
                  flat
                  class="form-control"
                  v-model="selectedCompany.preferred_currency"
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <span>
                  Tags:
                  <span class="overline text-lowercase primary--text"
                    >tap enter after a tag word to add (max 3 tags)</span
                  >
                </span>
                <input-tag
                  placeholder="add tag"
                  :before-adding="
                    (tag) => tag.trim().toLowerCase().replace(/\s+/g, '-')
                  "
                  v-model="selectedCompany.tags"
                ></input-tag>
              </v-col>
            </v-row>

            <v-divider class="my-1 py-1"></v-divider>

            <v-row>
              <span class="pl-4 body primary--text">Primary Contact</span>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6" md="3">
                <span>First name:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.contact_info.first_name"
                />
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <span>Last name:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.contact_info.last_name"
                />
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <span>Email:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.contact_info.email"
                />
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <span>Phone:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.contact_info.phone"
                />
              </v-col>
            </v-row>

            <v-divider class="my-1 py-1"></v-divider>

            <v-row no-gutters>
              <v-col>
                <span class="pl-4 body primary--text">Contact Persons</span>
              </v-col>

              <v-col class="d-flex justify-content-end pr-4">
                <span>Add another person</span>
                <v-btn
                  color="primary"
                  x-small
                  outlined
                  depressed
                  tile
                  class="ml-4"
                  :disabled="selectedCompany.contact_persons.length >= 4"
                  @click="addAnotherPerson"
                >
                  <v-icon small>add</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <template v-for="(cp, ind) in selectedCompany.contact_persons">
              <v-row :key="ind">
                <v-col cols="3" class="pr-0">
                  <span>First name:</span>
                  <input class="form-control" v-model="cp.first_name" />
                </v-col>

                <v-col cols="3">
                  <span>Last name:</span>
                  <input class="form-control" v-model="cp.last_name" />
                </v-col>

                <v-col cols="2" class="px-0">
                  <span>Email:</span>
                  <input class="form-control" v-model="cp.email" />
                </v-col>

                <v-col cols="2">
                  <span>Phone:</span>
                  <input class="form-control" v-model="cp.phone" />
                </v-col>

                <v-col cols="1" class="pr-0">
                  <v-switch
                    color="primary"
                    v-model="cp.notify"
                    label="Notify"
                  ></v-switch>
                </v-col>

                <v-col class="d-flex justify-end px-0">
                  <v-btn
                    color="error"
                    small
                    icon
                    class="mt-2"
                    @click="removePerson(cp)"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </template>

            <v-divider class="my-1 py-1"></v-divider>

            <v-row>
              <v-col cols="12" sm="6" md="5">
                <span>Address Line 1:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.address.line_1"
                />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <span>Address Line 2:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.address.line_2"
                />
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <span>Address Line 3:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.address.line_3"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6" md="3">
                <span>City:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.address.city"
                />
              </v-col>

              <v-col cols="12" sm="6" md="2">
                <span>Postcode:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.address.postcode"
                />
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <span>State:</span>
                <input
                  class="form-control"
                  v-model="selectedCompany.address.state"
                />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <span>Country:</span>
                <v-autocomplete
                  :items="sortedCountriesList"
                  class="form-control"
                  v-model="selectedCompany.address.code_country"
                  item-value="code"
                  item-text="name"
                  clearable
                ></v-autocomplete>
              </v-col>
            </v-row>

            <v-row v-if="isAdmin">
              <v-col>
                <div
                  class="custom-control custom-checkbox"
                  style="height: 36px; padding-top: 10px; display: inline-block"
                >
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="approved"
                    v-model="selectedCompany.approved"
                  />
                  <label class="custom-control-label" for="approved"
                    >Approve</label
                  >
                </div>
              </v-col>

              <v-col>
                <div
                  class="custom-control custom-checkbox"
                  style="height: 36px; padding-top: 10px; display: inline-block"
                >
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="accessallowed"
                    v-model="selectedCompany.allow_access"
                  />
                  <label class="custom-control-label" for="accessallowed"
                    >Allowed Access</label
                  >
                </div>
              </v-col>

              <!-- <v-col>
                <div
                  class="custom-control custom-checkbox"
                  style="height: 36px; padding-top: 10px; display: inline-block"
                >
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="xerosync"
                    v-model="selectedCompany.xero_sync"
                  />
                  <label class="custom-control-label" for="xerosync">Xero Sync</label>
                </div>
              </v-col> -->
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider class="my-0"></v-divider>

      <v-card-actions class="mb-3">
        <v-row justify="end">
          <v-btn
            @click="onSave"
            class="mr-2 font-weight-bold"
            color="primary"
            tile
            depressed
            >SAVE</v-btn
          >
          <v-btn
            @click="onDialogClose"
            class="ml-2 font-weight-bold"
            color="error"
            tile
            depressed
            >CLOSE</v-btn
          >
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import constant from "../../const"
import InputTag from "vue-input-tag"

export default {
  props: ["dialog", "company"],
  components: { InputTag },
  data() {
    return {
      snackbarColor: "",
      showSnackbar: false,
      snackbarMsg: "",
      snackbarTimeout: 8000,

      alert: false,
      alertType: "success",
      alertMessage: "",
      title: "",

      defaultSelectedCompany: {
        company_id: null,
        name: null,
        short_name: null,
        registration_number: null,
        preferred_currency: "MYR",
        tags: [],
        address: {
          line_1: null,
          line_2: null,
          line_3: null,
          city: null,
          postcode: null,
          state: null,
          code_country: "MY",
        },
        contact_info: {
          first_name: null,
          last_name: null,
          email: null,
          phone: null,
          notify: true,
        },
        contact_persons: [],
      },

      selectedCompany: null,
      updatedCompany: null,
      companyUpdated: false,

      countries: [],
      currencies: [],
    }
  },

  created() {},
  mounted() {},

  watch: {
    dialog: function () {
      if (this.dialog) {
        this.companyUpdated = false
        // fetch countries
        this.loadCountriesList()
        // fetch currencies
        this.loadCurrenciesList()

        if (this.company != null) {
          this.selectedCompany = this.company
        } else {
          this.selectedCompany = this.defaultSelectedCompany
        }

        if (this.selectedCompany.company_id == null) {
          this.title = "Create New Company"
        } else {
          this.title = `Edit ${this.selectedCompany.name} (${this.selectedCompany.company_id})`
        }
        // console.log("selected: ", this.selectedCompany);
      }
    },
  },

  methods: {
    /**
     * Get country list
     */
    loadCountriesList() {
      this.$https.get("/api/countries").then((resp) => {
        if (resp.data.status == constant.RESPONSE_STATUS_OK) {
          this.countries = resp.data.data
        } else {
          this.countries = []
        }
      })
    },

    /**
     * Get currencies list
     */
    loadCurrenciesList() {
      this.$https.get("/api/currencies/list/dropdown").then((resp) => {
        if (resp.data.status == constant.RESPONSE_STATUS_OK) {
          this.currencies = resp.data.data
        } else {
          this.currencies = []
        }
      })
    },

    resetCompany() {
      this.selectedCompany = {
        company_id: null,
        name: null,
        short_name: null,
        registration_number: null,
        preferred_currency: "MYR",
        tags: [],
        address: {
          line_1: null,
          line_2: null,
          line_3: null,
          city: null,
          postcode: null,
          state: null,
          code_country: "MY",
        },
        contact_info: {
          first_name: null,
          last_name: null,
          email: null,
          phone: null,
          notify: false,
        },
        contact_persons: [],
      }
    },

    /**
     * Add another contact person
     */
    addAnotherPerson() {
      // push a new contact person object
      this.selectedCompany.contact_persons.push({
        first_name: null,
        last_name: null,
        email: null,
        phone: null,
        notify: false,
      })
    },

    /**
     * Remove contact person
     */
    removePerson(person) {
      // remove give person from the list
      this.selectedCompany.contact_persons =
        this.selectedCompany.contact_persons.filter((obj) => obj != person)
    },

    onDialogClose() {
      this.$emit(
        "dialog-close",
        constant.DIALOG_COMPANY,
        this.companyUpdated,
        this.updatedCompany
      )
    },

    onSave() {
      this.$root.blockUImsg = "Saving..."
      this.$root.blockUIshow = true

      // create new company
      if (this.selectedCompany.company_id == null) {
        this.$https
          .post(`/api/company/add`, this.selectedCompany)
          .then((resp) => {
            if (resp.data.status == constant.RESPONSE_STATUS_OK) {
              this.companyUpdated = true
              this.updatedCompany = resp.data.data
              this.selectedCompany.company_id = this.updatedCompany.company_id
              this.title = `Edit ${this.selectedCompany.name} (${this.selectedCompany.company_id})`
              this.showSnackbar = true
              this.snackbarColor = "success"
              this.snackbarMsg = `${this.selectedCompany.name} added successfully`
            } else {
              this.showSnackbar = true
              this.snackbarColor = "error"
              this.snackbarMsg = resp.data.msg
            }
          })
          .catch((err) => {
            this.showSnackbar = true
            this.snackbarColor = "error"
            this.snackbarMsg = err
          })
          .finally(() => {
            this.$root.blockUIshow = false
          })
      } else {
        this.$https
          .put(
            `/api/company/${this.selectedCompany.company_id}`,
            this.selectedCompany
          )
          .then((resp) => {
            if (resp.data.status == constant.RESPONSE_STATUS_OK) {
              this.updatedCompany = resp.data.data
              this.companyUpdated = true
              this.showSnackbar = true
              this.snackbarColor = "success"
              this.snackbarMsg = "Successfully updated company details"
            } else {
              this.showSnackbar = true
              this.snackbarColor = "error"
              this.snackbarMsg = resp.data.msg
            }
          })
          .catch((err) => {
            this.showSnackbar = true
            this.snackbarColor = "error"
            this.snackbarMsg = err
          })
          .finally(() => {
            this.$root.blockUIshow = false
          })
      }
    },
  },

  computed: {
    sortedCountriesList: function () {
      return this.countries.sort(function (a, b) {
        return ("" + a.name).localeCompare(b.name)
      })
    },

    isAdmin() {
      return this.$root.is_admin
    },
  },
}
</script>

<style>
.v-btn-style {
  text-transform: none;
  text-align: left;
  /* font-size: 90%; */
}

.admin-dialog-container {
  padding: 0 30px;
}

.admin-dialog-container .sub-container-row-item {
  padding: 2px 20px;
}

.admin-dialog-container .sub-container-row-item .flex {
  line-height: 2.5;
}

.vue-input-tag-wrapper .input-tag {
  background-color: #009688;
  border: 2px solid white;
  color: white;
  display: inline-block;
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 4px;
  margin-right: 4px;
  padding: 3px;
}

.vue-input-tag-wrapper .input-tag .remove {
  cursor: pointer;
  color: #e0e0e0;
}
</style>
